import { useSolarCalc } from "./calculations";
import moment from "moment-timezone";
import { months } from "./month";

const calc = useSolarCalc();

export const guaDecrement = 0.0055;

export const convertPercentage = (dec) => calc.parseFormatNumber(dec * 100, 3);

export const convertDecimal = (percentage) =>
	calc.parseFormatNumber(percentage / 100, 4);

/**
 * Apply decrement with value
 * @param {number} value
 * @param {number} decrement
 * @returns value with decrement applied
 */
export const applyDecrement = (value, decrement, dp) =>
	calc.parseFormatNumber(value * (1 - decrement), dp);

/**
 * Calculate Pvsyst Productible
 * @param {number} totalEGrid
 * @param {number} capacity
 * @returns
 */
export const calculatePvsystProd = (totalEGrid, capacity) =>
	calc.parseFormatNumber((totalEGrid / capacity) * 1000);

/**
 * Calculate monthly target production based on year input
 * @param {number} asbuiltProd
 * @param {number} capacity
 * @param {number} summedCorr
 * @param {number} irradiance
 * @param {array} unused
 * @param {array} monthArray
 * @param {number} noOfYears
 * @returns
 */
export const calculateTargetProd = (
	egrid,
	capacity,
	summedCorr,
	unused,
	yearlyDecrement,
	siteCOD,
	noOfYears
) => {
	const cod = moment(siteCOD).format("DD-MMMM-YYYY");
	const codSplit = cod.split("-");
	const codDate = codSplit[0];
	const nextMonth = moment(siteCOD).add(1, "month");

	const codMonth =
		codDate === "01"
			? codSplit[1].toLocaleLowerCase()
			: nextMonth.format("MMMM").toLocaleLowerCase();

	const startYear =
		codDate === "01"
			? parseInt(codSplit[2])
			: parseInt(nextMonth.format("YYYY"));

	const unusedData = Object.entries(formatMonthlyData(unused, "unused"));
	const egridData = Object.entries(formatMonthlyData(egrid, "egrid"));

	// Has different unused %
	const monthlyUnused = unusedData;

	// E Grid values
	const egridValues = egridData.map((egrid) => egrid[1]);

	const totalEGrid = egridValues.reduce((a, b) => a + b);
	const asbuiltProd = calc.parseFormatNumber((totalEGrid / capacity) * 1000);

	// Calculate base BP production
	const bpProd = calculateBPProdYear(asbuiltProd, capacity, summedCorr);

	// Calculate asbuild yearly production
	const abProd = calc.parseFormatNumber(asbuiltProd * capacity, 1);

	// Calculate monthly BP expected production
	const monthlyExpBP = handleMonthlyTargets(
		bpProd,
		egridData,
		monthlyUnused,
		startYear
	);

	// Calculate monthly asbuilt expected production
	const monthlyExpAB = handleMonthlyTargets(
		abProd,
		egridData,
		false,
		startYear
	);

	// Sort months with cod month as first month if on 1st of month
	const sortedMonths = sortMonths(months, codMonth);

	// Sort results according to sorted months
	const sortedExpResultBP = sortResultByMonth(monthlyExpBP, sortedMonths);
	const sortedExpResultAB = sortResultByMonth(monthlyExpAB, sortedMonths);

	// Set years of every singl months
	const finalExpResultBP = setYearOfData(sortedExpResultBP);
	const finalExpResultAB = setYearOfData(sortedExpResultAB);

	// Calculate Guaranteed production
	// const sortedGuaResult = sortedExpResult.map((res) => [
	// 	res[0],
	// 	applyDecrement(res[1], guaDecrement,3),
	// ]);

	let tempResultsBP = finalExpResultBP;
	let tempResultsAB = finalExpResultAB;

	const BPArray = [];
	const ABArray = [];

	// Apply decrement for following years
	for (let i = 0; i < noOfYears; i++) {
		if (i === 0) {
			BPArray.push(tempResultsBP);
			ABArray.push(tempResultsAB);
		} else {
			const decrement = yearlyDecrement;

			// Apply decrement for all monthly production
			const BPResult = tempResultsBP.map((row) =>
				calculateOtherYears(row, decrement)
			);
			const ABResult = tempResultsAB.map((row) =>
				calculateOtherYears(row, decrement)
			);

			BPArray.push(BPResult);
			ABArray.push(ABResult);

			// Set the current values to be applied the decrement on next iteration
			tempResultsBP = BPResult;
			tempResultsAB = ABResult;
		}
	}

	return [BPArray, ABArray];
};

/**
 * Calculate BP production
 * @param {number} asbuiltProd
 * @param {number} capacity
 * @param {number} totalCorr
 * @param {number} unused
 * @returns BP production
 */
const calculateBPProdYear = (asbuiltProd, capacity, totalCorr) => {
	const baseBPProd = calc.parseFormatNumber(
		applyDecrement(asbuiltProd, totalCorr, 3),
		1
	);

	const result = calc.parseFormatNumber(baseBPProd * capacity, 1);

	return result;
};

/**
 * Get monthly target entries
 * @param {number} bpProd
 * @param {array} irradiance
 * @param {array} unused
 * @returns monthly targets entries
 */
const handleMonthlyTargets = (bpProd, egrid, unused, startYear) => {
	const unusedObj = unused ? Object.fromEntries(unused) : false;

	// Calculate total E Grid
	const egridValues = egrid.map((egrid) => egrid[1]);
	const totalEGrid = egridValues.reduce((a, b) => a + b);

	// Calculate monthly based on weightage
	const monthlyTarget = egrid.map((eg) => {
		const weightage = eg[1] / totalEGrid;

		const month = eg[0].split("_")[0];
		const monthNo = moment(month, "MMMM").format("M");

		let monthlyProd = bpProd * weightage;

		// Apply unused decrement if there are unused passed
		if (unused) {
			const unusedKey = `${month}_unused`;
			const dec = unusedObj[unusedKey];

			monthlyProd = applyDecrement(monthlyProd, dec, 3);
		}

		monthlyProd = calc.parseFormatNumber(monthlyProd, 2);

		return { month, monthNo, monthlyProd, year: startYear };
	});

	return monthlyTarget;
};

/**
 * Sort month array with COD month as 1st month
 * @param {array} monthArray
 * @param {string} firstMonth
 * @returns month array with COD month as 1st month
 */
export const sortMonths = (monthArray, firstMonth) => {
	const index = monthArray.findIndex((month) => month === firstMonth);
	let finalArray = monthArray;

	// Reorder months array with COD month as first month
	if (index !== -1) {
		const firstHalfArray = monthArray.filter((month, i) => i >= index);
		const secondHalfArray = monthArray.filter((month, i) => i < index);
		finalArray = [...firstHalfArray, ...secondHalfArray];
	}

	return finalArray;
};

const setYearOfData = (dataArr) => {
	let nextYear = false;
	const finalArr = dataArr.map((monthRow) => {
		// Add 1 year to months of the new year
		const year = nextYear ? monthRow.year + 1 : monthRow.year;

		// Check if month is December
		const isDecember = monthRow.monthNo == 12;

		// Set nextYear as true only if it was false initially
		if (!nextYear) nextYear = isDecember;

		return { ...monthRow, year };
	});

	return finalArr;
};
/**
 * Sort months based on month array
 * @param {array} arr
 * @param {array} monthArray
 * @returns Sorted array
 */
const sortResultByMonth = (arr, monthArray) =>
	arr.sort((a, b) => monthArray.indexOf(a.month) - monthArray.indexOf(b.month));

const calculateOtherYears = (row, decrement) => {
	const finalRow = {
		...row,
		monthlyProd: applyDecrement(row.monthlyProd, decrement, 3),
		year: row.year + 1,
	};

	return finalRow;
};
/**
 * Format target data
 * @param {obj} paramObj
 * @param {obj} irrObj
 * @param {obj} unusedObj
 * @param {obj} tempObj
 * @param {obj} egridObj
 * @returns formatted target data
 */
export const formatData = (paramObj, irrObj, unusedObj, tempObj, egridObj) => {
	const paramData = paramObj;
	const irrData = formatMonthlyData(irrObj, "irr");
	const unusedData = formatMonthlyData(unusedObj, "unused");
	const tempData = formatMonthlyData(tempObj, "temp");
	const egridData = formatMonthlyData(egridObj, "egrid");

	const combinedData = {
		...paramData,
		...egridData,
		...irrData,
		...unusedData,
		...tempData,
	};
	return combinedData;
};

/**
 * Format target monthly data
 * @param {obj} obj
 * @returns
 */
export const formatMonthlyData = (obj, name) => {
	delete obj.id;
	delete obj.table_name;
	const entries = Object.entries(obj);

	const formattedEntries = entries.map((entry) => [
		`${entry[0]}_${name}`,
		entry[1],
	]);
	const formattedObject = Object.fromEntries(formattedEntries);

	return formattedObject;
};
